import {
  PROGRESS_MAX_BYTES,
  MAX_REVISION,
  cloneProgress,
  serializedProgressBytes,
  validateProgress,
  type Progress,
} from "./schema";
import { migrateProgress } from "./migration";

export const GUEST_PROGRESS_KEY = "snake:progress:guest:v1";
export const PROGRESS_MIGRATION_KEY = "snake:progress:migration:v1";
export const AUTO_RESUME_BLOCK_KEY = "snake:auth:auto-resume-blocked:v1";
const AUTO_RESUME_BLOCK_VALUE = "blocked";
const ENVELOPE_FIELDS = new Set([
  "progress",
  "savedAtClient",
  "baseCloudRevision",
  "dirty",
  "deviceId",
]);

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LocalProgressEnvelope {
  progress: Progress;
  savedAtClient: number;
  baseCloudRevision: number;
  dirty: boolean;
  deviceId: string;
}

export interface LocalReadResult {
  envelope: LocalProgressEnvelope | null;
  recoveredFromCorruption: boolean;
  persistenceAvailable: boolean;
}

export interface AutoResumeBlockReadResult {
  blocked: boolean;
  persistenceAvailable: boolean;
}

export const uidProgressKey = (uid: string): string => {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(uid)) {
    throw new Error("invalid Firebase UID for progress storage");
  }
  return `snake:progress:uid:${uid}:v1`;
};

type LocalProgressEnvelopeCandidate = Omit<
  LocalProgressEnvelope,
  "progress"
> & { progress: unknown };

const isEnvelopeCandidate = (
  input: unknown,
): input is LocalProgressEnvelopeCandidate => {
  if (typeof input !== "object" || input === null || Array.isArray(input))
    return false;
  const value = input as Record<string, unknown>;
  const keys = Object.keys(value);
  return (
    keys.length === ENVELOPE_FIELDS.size &&
    keys.every((key) => ENVELOPE_FIELDS.has(key)) &&
    "progress" in value &&
    typeof value.savedAtClient === "number" &&
    Number.isSafeInteger(value.savedAtClient) &&
    value.savedAtClient >= 0 &&
    typeof value.baseCloudRevision === "number" &&
    Number.isSafeInteger(value.baseCloudRevision) &&
    value.baseCloudRevision >= 0 &&
    value.baseCloudRevision <= MAX_REVISION &&
    typeof value.dirty === "boolean" &&
    typeof value.deviceId === "string" &&
    /^[A-Za-z0-9_-]{1,128}$/.test(value.deviceId)
  );
};

export class LocalProgressStore {
  constructor(
    private readonly storage: StorageAdapter,
    private readonly now: () => number = Date.now,
    private readonly createDeviceId: () => string = () => crypto.randomUUID(),
  ) {}

  read(key: string): LocalReadResult {
    let raw: string | null;
    try {
      raw = this.storage.getItem(key);
    } catch {
      return {
        envelope: null,
        recoveredFromCorruption: false,
        persistenceAvailable: false,
      };
    }
    if (!raw)
      return {
        envelope: null,
        recoveredFromCorruption: false,
        persistenceAvailable: true,
      };
    try {
      if (serializedProgressBytes(raw) > PROGRESS_MAX_BYTES)
        throw new Error("oversized envelope");
      const parsed: unknown = JSON.parse(raw);
      if (isEnvelopeCandidate(parsed)) {
        const migration = migrateProgress(parsed.progress, this.now());
        if (!migration) throw new Error("invalid envelope progress");
        const envelope: LocalProgressEnvelope = {
          ...parsed,
          progress: cloneProgress(migration.progress),
          dirty: migration.migrated ? true : parsed.dirty,
        };
        let persistenceAvailable = true;
        if (migration.migrated) {
          const migratedRaw = JSON.stringify(envelope);
          if (serializedProgressBytes(migratedRaw) > PROGRESS_MAX_BYTES) {
            persistenceAvailable = false;
          } else {
            try {
              this.storage.setItem(key, migratedRaw);
            } catch {
              persistenceAvailable = false;
            }
          }
        }
        return {
          envelope: {
            ...envelope,
            progress: cloneProgress(envelope.progress),
          },
          recoveredFromCorruption: false,
          persistenceAvailable,
        };
      }
      const migration = migrateProgress(parsed, this.now());
      if (!migration?.migrated) throw new Error("invalid envelope");
      const envelope = this.createEnvelope(migration.progress, 0, true);
      const migratedRaw = JSON.stringify(envelope);
      let persistenceAvailable = true;
      if (serializedProgressBytes(migratedRaw) > PROGRESS_MAX_BYTES) {
        persistenceAvailable = false;
      } else {
        try {
          this.storage.setItem(key, migratedRaw);
        } catch {
          persistenceAvailable = false;
        }
      }
      return {
        envelope,
        recoveredFromCorruption: false,
        persistenceAvailable,
      };
    } catch {
      this.quarantine(key, raw);
      return {
        envelope: null,
        recoveredFromCorruption: true,
        persistenceAvailable: true,
      };
    }
  }

  write(
    key: string,
    progress: Progress,
    baseCloudRevision: number,
    dirty: boolean,
    existingDeviceId?: string,
  ): LocalProgressEnvelope {
    const envelope = this.createEnvelope(
      progress,
      baseCloudRevision,
      dirty,
      existingDeviceId,
    );
    const raw = JSON.stringify(envelope);
    if (serializedProgressBytes(raw) > PROGRESS_MAX_BYTES) {
      throw new Error("progress envelope exceeds the maximum serialized size");
    }
    this.storage.setItem(key, raw);
    return envelope;
  }

  private createEnvelope(
    progress: Progress,
    baseCloudRevision: number,
    dirty: boolean,
    existingDeviceId?: string,
  ): LocalProgressEnvelope {
    const validated = validateProgress(progress);
    if (!validated.ok) throw new Error(validated.reason);
    if (
      !Number.isSafeInteger(baseCloudRevision) ||
      baseCloudRevision < 0 ||
      baseCloudRevision > MAX_REVISION
    ) {
      throw new Error("baseCloudRevision is invalid");
    }
    const envelope: LocalProgressEnvelope = {
      progress: cloneProgress(validated.value),
      savedAtClient: this.now(),
      baseCloudRevision,
      dirty,
      deviceId: existingDeviceId ?? this.createDeviceId(),
    };
    return envelope;
  }

  remove(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch {
      // Storage failure must not escape into gameplay.
    }
  }

  readAutoResumeBlock(): AutoResumeBlockReadResult {
    try {
      return {
        blocked: this.storage.getItem(AUTO_RESUME_BLOCK_KEY) !== null,
        persistenceAvailable: true,
      };
    } catch {
      return { blocked: true, persistenceAvailable: false };
    }
  }

  blockAutoResume(): boolean {
    try {
      this.storage.setItem(AUTO_RESUME_BLOCK_KEY, AUTO_RESUME_BLOCK_VALUE);
      return true;
    } catch {
      return false;
    }
  }

  clearAutoResumeBlock(): boolean {
    try {
      this.storage.removeItem(AUTO_RESUME_BLOCK_KEY);
      return true;
    } catch {
      return false;
    }
  }

  backupCandidate(source: "local" | "cloud", progress: Progress): void {
    try {
      const validated = validateProgress(progress);
      if (!validated.ok) return;
      const raw = JSON.stringify(validated.value);
      if (serializedProgressBytes(raw) > PROGRESS_MAX_BYTES) return;
      this.storage.setItem(
        `snake:progress:conflict-backup:${this.now()}:${source}`,
        raw,
      );
    } catch {
      // Backups are best effort and cannot block an explicit conflict decision.
    }
  }

  writeRawForMigration(key: string, raw: string): void {
    if (serializedProgressBytes(raw) > PROGRESS_MAX_BYTES)
      throw new Error("migration value is too large");
    this.storage.setItem(key, raw);
  }

  private quarantine(key: string, raw: string): void {
    try {
      this.storage.setItem(
        `snake:progress:quarantine:${this.now()}`,
        raw.slice(0, PROGRESS_MAX_BYTES),
      );
      this.storage.removeItem(key);
    } catch {
      // The in-memory fallback remains available.
    }
  }
}
